import React, { useEffect, useCallback, useRef, useState } from 'react';
import { scrollEditorToElement } from '../../utils/scrollEditor';
import { useAuth } from '../../context/AuthContext';

/**
 * Section navigation sidebar — hardware-inspired vertical menu.
 *
 * Sits to the left of EditorPanel inside the device frame.
 * Each item: icon (20px SVG) + small uppercase label + LED dot.
 * Active LED = emerald glow. Inactive LED = dim.
 *
 * Clicking a section scrolls the editor panel to that anchor.
 * Scroll-spy tracks which section is currently visible.
 *
 * 12 sections:
 *   media   → Carousels + Slides (scrolls to very top)
 *   normies → Normies Studio
 *   bg      → Solid, Gradient, Generative, 3D, Backgrounds
 *   layout  → Template picker
 *   fx      → Glass effect
 *   type    → Font preset
 *   header  → Title + Kicker
 *   text    → Subtitle + Body
 *   color   → Color palette
 *   logo    → Logo editor
 *   blocks  → Bullets, tags, widgets (progress, stats, callout, image, video)
 *   footer  → CTA
 */

const SECTIONS = [
  {
    id: 'media',
    label: 'MEDIA',
    scrollToTop: true,
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="7" height="7" rx="1.5" />
        <rect x="11" y="2" width="7" height="7" rx="1.5" />
        <rect x="2" y="11" width="7" height="7" rx="1.5" />
        <rect x="11" y="11" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    id: 'showcase',
    label: 'SHOWCASE',
    adminOnly: true,
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 2l2.47 5.01L18 7.75l-4 3.9.94 5.49L10 14.39l-4.94 2.75L6 11.65l-4-3.9 5.53-.74z" />
      </svg>
    ),
  },
  {
    id: 'normies',
    label: 'NORMIES',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" shapeRendering="crispEdges">
        {/* Normies "n" logo silhouette — blocky pixel lowercase n */}
        <rect x="7" y="2" width="8" height="3" />
        <rect x="15" y="2" width="3" height="16" />
        <rect x="3" y="5" width="3" height="13" />
        <rect x="9" y="9" width="3" height="5" />
      </svg>
    ),
  },
  {
    id: 'bg',
    label: 'BG',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="16" height="16" rx="2" />
        <path d="M2 14l4-4 3 3 4-5 5 6" />
        <circle cx="7" cy="7" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'layout',
    label: 'LAYOUT',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="16" height="5" rx="1.5" />
        <rect x="2" y="10" width="7" height="8" rx="1.5" />
        <rect x="11" y="10" width="7" height="8" rx="1.5" />
      </svg>
    ),
  },
  {
    id: 'fx',
    label: 'FX',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 2l1.5 5.5L17 9l-5.5 1.5L10 16l-1.5-5.5L3 9l5.5-1.5z" />
      </svg>
    ),
  },
  {
    id: 'type',
    label: 'TYPE',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 16V4h4.5a3.5 3.5 0 0 1 0 7H4" />
        <path d="M4 11h5.5a3.5 3.5 0 0 1 0 7H4" />
      </svg>
    ),
  },
  {
    id: 'header',
    label: 'HEADER',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="5" x2="10" y2="5" />
        <line x1="3" y1="10" x2="17" y2="10" />
        <line x1="3" y1="15" x2="13" y2="15" />
      </svg>
    ),
  },
  {
    id: 'text',
    label: 'TEXT',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="4" x2="17" y2="4" />
        <line x1="3" y1="8" x2="14" y2="8" />
        <line x1="3" y1="12" x2="17" y2="12" />
        <line x1="3" y1="16" x2="11" y2="16" />
      </svg>
    ),
  },
  {
    id: 'color',
    label: 'COLOR',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="8" />
        <circle cx="10" cy="6" r="1.5" fill="currentColor" />
        <circle cx="6.5" cy="11" r="1.5" fill="currentColor" />
        <circle cx="13" cy="12" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'blocks',
    label: 'BLOCKS',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="7" height="5" rx="1" />
        <rect x="11" y="2" width="7" height="5" rx="1" />
        <rect x="2" y="9" width="16" height="4" rx="1" />
        <rect x="2" y="15" width="16" height="3" rx="1" />
      </svg>
    ),
  },
  {
    id: 'logo',
    label: 'LOGO',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="14" height="14" rx="3" />
        <path d="M7 13l2.5-4 2 2.5L14 8" />
      </svg>
    ),
  },
  {
    id: 'footer',
    label: 'FOOTER',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="12" width="16" height="6" rx="1.5" />
        <line x1="6" y1="15" x2="14" y2="15" />
        <line x1="4" y1="4" x2="16" y2="4" />
        <line x1="4" y1="8" x2="12" y2="8" />
      </svg>
    ),
  },
];

const SECTION_IDS = SECTIONS.map(s => s.id);

export function SideNav() {
  const { isSeed } = useAuth();
  const [activeId, setActiveId] = useState('media');
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Filter sections: admin-only sections only visible to seed user
  const visibleSections = isSeed
    ? SECTIONS
    : SECTIONS.filter(s => !s.adminOnly);
  const visibleIds = visibleSections.map(s => s.id);

  useEffect(() => {
    const findContainer = () => document.getElementById('editor-scroll-container');

    const computeActive = (container) => {
      if (isScrollingRef.current) return;

      // When scrolled to the bottom, activate the last section whose element exists.
      // This fixes the last (short) section never reaching the threshold.
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 4;
      if (atBottom) {
        for (let i = SECTION_IDS.length - 1; i >= 0; i--) {
          if (document.getElementById(`section-${SECTION_IDS[i]}`)) {
            setActiveId(SECTION_IDS[i]);
            return;
          }
        }
      }

      const containerRect = container.getBoundingClientRect();
      const threshold = containerRect.top + 100;

      let current = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(`section-${id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= threshold) {
            current = id;
          }
        }
      }
      setActiveId(current);
    };

    const handleScroll = () => {
      const container = containerRef.current;
      if (container) computeActive(container);
    };

    let container = findContainer();
    if (container) {
      containerRef.current = container;
      container.addEventListener('scroll', handleScroll, { passive: true });
      computeActive(container);
      return () => container.removeEventListener('scroll', handleScroll);
    }

    const interval = setInterval(() => {
      const el = findContainer();
      if (el) {
        clearInterval(interval);
        containerRef.current = el;
        el.addEventListener('scroll', handleScroll, { passive: true });
        computeActive(el);
      }
    }, 150);

    return () => {
      clearInterval(interval);
      const c = containerRef.current;
      if (c) c.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleClick = useCallback((section) => {
    const container = containerRef.current || document.getElementById('editor-scroll-container');
    if (!container) return;

    isScrollingRef.current = true;
    setActiveId(section.id);

    if (section.scrollToTop) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const el = document.getElementById(`section-${section.id}`);
      if (el) scrollEditorToElement(el);
    }

    clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 600);
  }, []);

  return (
    <nav className="side-nav" aria-label="Editor sections">
      {visibleSections.map((section) => {
        const isActive = activeId === section.id;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => handleClick(section)}
            className={`side-nav-item ${isActive ? 'side-nav-item-active' : ''}`}
            aria-current={isActive ? 'true' : undefined}
            title={section.label}
          >
            <span className="side-nav-led" />
            <span className="side-nav-icon">{section.icon}</span>
            <span className="side-nav-label">{section.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
